/**
 * Normalizes GET /destinations/:id JSON for the city page UI.
 */
export function normalizeDestination(raw) {
  if (!raw || typeof raw !== "object") return null;
  const heroImages =
    Array.isArray(raw.heroImages) && raw.heroImages.length
      ? raw.heroImages.filter(Boolean)
      : raw.imageUrl
        ? [raw.imageUrl]
        : [];

  const toInsight = (items, legacyList) => {
    if (Array.isArray(items) && items.length) {
      return items.map((it) => ({
        title: (it.title || "").trim(),
        body: (it.body || "").trim(),
      }));
    }
    return (legacyList || []).map((s) => {
      const str = String(s || "").trim();
      const colon = str.indexOf(":");
      if (colon > 0 && colon < 80) {
        return { title: str.slice(0, colon).trim(), body: str.slice(colon + 1).trim() };
      }
      return { title: "", body: str };
    });
  };

  const budget = raw.budget && typeof raw.budget === "object" ? raw.budget : {};
  const travel = raw.travel && typeof raw.travel === "object" ? raw.travel : {};
  const climate = raw.climate && typeof raw.climate === "object" ? raw.climate : {};
  const visaSection = raw.visaSection && typeof raw.visaSection === "object" ? raw.visaSection : {};
  const arrival = raw.arrival && typeof raw.arrival === "object" ? raw.arrival : {};
  const essentialAppsRaw = Array.isArray(raw.essentialApps) ? raw.essentialApps : [];

  return {
    ...raw,
    countryCode: (() => {
      const c = String(raw.countryCode || "")
        .trim()
        .toUpperCase()
        .slice(0, 2);
      return c.length === 2 ? c : "";
    })(),
    heroImages,
    highlights: toInsight(raw.highlights, raw.pros),
    challenges: toInsight(raw.challenges, raw.cons),
    notFor: stringLinesFromMixedArray(raw.notFor),
    proTip: raw.proTip || "",
    neighborhoods: Array.isArray(raw.neighborhoods) ? raw.neighborhoods.map((n) => ({ ...n })) : [],
    budget: {
      rent: budget.rent ?? null,
      food: budget.food ?? null,
      coworking: budget.coworking ?? null,
      transport: budget.transport ?? null,
    },
    travel: {
      flightFrom: travel.flightFrom || "GRU",
      flightPrice: travel.flightPrice ?? 0,
      flightDuration: travel.flightDuration || "",
      flightNote: travel.flightNote || "",
      visaEase: travel.visaEase || "",
      visaDuration: travel.visaDuration || "",
      timezoneOffset: travel.timezoneOffset || "",
      timezoneNote: travel.timezoneNote || "",
    },
    climate: {
      bestTimeLabel: climate.bestTimeLabel || "",
      monthlyHighs: Array.isArray(climate.monthlyHighs) ? climate.monthlyHighs.slice(0, 12) : [],
      highlightMonth: climate.highlightMonth != null ? climate.highlightMonth : 4,
      tooltipTemp: climate.tooltipTemp || "",
    },
    vitalStats:
      Array.isArray(raw.vitalStats) && raw.vitalStats.length
        ? raw.vitalStats.map((s) => ({ ...s }))
        : [
            { label: "Infraestrutura para trabalho remoto", value: Number(raw.scoreInternet) || 0, max: 10, display: "" },
            { label: "Custo-benefício", value: Number(raw.scoreCost) || 0, max: 10, display: "" },
            { label: "Estilo de vida e lazer", value: Number(raw.scoreOverall) || 0, max: 10, display: "" },
            { label: "Internet", value: Number(raw.scoreInternet) || 0, max: 10, display: "" },
          ],
    planningLink: raw.planningLink || "",
    communityLink: raw.communityLink || "",
    guideLink: raw.guideLink || "",
    ctaSubtitle: raw.ctaSubtitle || "",
    heroVibeLine: raw.heroVibeLine || "",
    essentialApps: essentialAppsRaw.map((a) => ({
      name: String(a?.name || "").trim(),
      description: String(a?.description || "").trim(),
      link: String(a?.link || "").trim(),
    })),
    visaSection: {
      visaSteps: Array.isArray(visaSection.visaSteps)
        ? visaSection.visaSteps.map((s) => String(s || "").trim()).filter(Boolean)
        : [],
      visaLink: String(visaSection.visaLink || "").trim(),
      visaCost: String(visaSection.visaCost || "").trim(),
      visaDifficulty: String(visaSection.visaDifficulty || "").trim(),
    },
    arrival: {
      arrivalTips: stringLinesFromMixedArray(arrival.arrivalTips),
      requiredDocuments: stringLinesFromMixedArray(arrival.requiredDocuments),
    },
    communityEnabled: !!raw.communityEnabled,
    communityDescription: String(raw.communityDescription || "").trim(),
    communityRules: stringLinesFromMixedArray(raw.communityRules),
    customSections: Array.isArray(raw.customSections)
      ? raw.customSections.map(s => ({
          id: s.id || 0,
          title: String(s.title || '').trim(),
          contentHtml: String(s.contentHtml || '').trim(),
          anchorSlug: String(s.anchorSlug || '').trim(),
          order: Number(s.order) || 0,
        }))
      : [],
  };
}

function insightToProsList(items) {
  return (items || [])
    .map((h) => {
      const t = (h.title || "").trim();
      const b = (h.body || "").trim();
      if (!t && !b) return "";
      return t ? `${t}: ${b}` : b;
    })
    .filter(Boolean);
}

/**
 * LLMs sometimes return notFor / list fields as objects { title, body } instead of plain strings.
 */
export function stringLinesFromMixedArray(value) {
  if (!Array.isArray(value)) return [];
  const out = [];
  for (const x of value) {
    if (x == null) continue;
    if (typeof x === "string") {
      const s = x.trim();
      if (s) out.push(s);
      continue;
    }
    if (typeof x === "object") {
      const title = String(x.title || "").trim();
      const body = String(x.body || x.text || x.description || x.reason || "").trim();
      const line = title && body ? `${title}: ${body}` : title || body;
      if (line) out.push(line);
    }
  }
  return out;
}

/**
 * LLMs sometimes return snake_case or a single newline-separated string.
 */
function visaSectionFromLooseDraft(v) {
  if (!v || typeof v !== "object") return null;
  const stepsRaw = v.visaSteps ?? v.visa_steps ?? v.steps;
  let visaSteps = [];
  if (Array.isArray(stepsRaw)) {
    visaSteps = stepsRaw.map((x) => String(x || "").trim()).filter(Boolean);
  } else if (typeof stepsRaw === "string" && stepsRaw.trim()) {
    visaSteps = stepsRaw
      .split(/\n+/)
      .map((s) => s.replace(/^\d+[.)\s]+/, "").trim())
      .filter(Boolean);
  }
  const visaLink = String(v.visaLink ?? v.visa_link ?? "").trim();
  const visaCost = String(v.visaCost ?? v.visa_cost ?? "").trim();
  const visaDifficulty = String(v.visaDifficulty ?? v.visa_difficulty ?? "").trim();
  return { visaSteps, visaLink, visaCost, visaDifficulty };
}

/**
 * Mescla o objeto `draft` de POST /admin/destinations/:id/ai-draft sobre o destino normalizado do admin.
 */
export function mergeAiCityDraft(base, draft) {
  if (!base || typeof base !== "object") return base;
  if (!draft || typeof draft !== "object") return normalizeDestination(base);

  const out = { ...base };
  const s = (v) => (v == null ? "" : String(v).trim());

  if (s(draft.name)) out.name = s(draft.name);
  if (s(draft.country)) out.country = s(draft.country);
  if (s(draft.countryCode)) {
    const cc = s(draft.countryCode).toUpperCase().slice(0, 2);
    if (cc.length === 2) out.countryCode = cc;
  }
  if (draft.region !== undefined && draft.region !== null) out.region = s(draft.region);

  if (draft.monthlyCost != null && draft.monthlyCost !== "") {
    const mc = Number(draft.monthlyCost);
    if (Number.isFinite(mc) && mc > 0) out.monthlyCost = mc;
  }

  for (const k of ["scoreOverall", "scoreCost", "scoreInternet"]) {
    if (draft[k] == null || draft[k] === "") continue;
    const n = Number(draft[k]);
    if (Number.isFinite(n)) out[k] = Math.max(0, Math.min(10, n));
  }

  if (Array.isArray(draft.tags)) {
    out.tags = draft.tags.map((t) => String(t || "").trim()).filter(Boolean);
  }

  if (Array.isArray(draft.heroImages)) {
    const imgs = draft.heroImages.map((u) => String(u || "").trim()).filter(Boolean);
    if (imgs.length) out.heroImages = imgs;
  }
  if (s(draft.imageUrl)) out.imageUrl = s(draft.imageUrl);

  if (Array.isArray(draft.highlights) && draft.highlights.length) {
    out.highlights = draft.highlights.map((h) => ({
      title: (h.title || "").trim(),
      body: (h.body || "").trim(),
    }));
    out.pros = insightToProsList(out.highlights);
  }
  if (Array.isArray(draft.challenges) && draft.challenges.length) {
    out.challenges = draft.challenges.map((h) => ({
      title: (h.title || "").trim(),
      body: (h.body || "").trim(),
    }));
    out.cons = insightToProsList(out.challenges);
  }

  // EN variants — bilingual draft (micro-sessão 3b)
  if (s(draft.description)) out.description = s(draft.description);
  if (s(draft.descriptionEn)) out.descriptionEn = s(draft.descriptionEn);
  if (Array.isArray(draft.pros)) out.pros = draft.pros.map((p) => String(p || "").trim()).filter(Boolean);
  if (Array.isArray(draft.prosEn)) out.prosEn = draft.prosEn.map((p) => String(p || "").trim()).filter(Boolean);
  if (Array.isArray(draft.cons)) out.cons = draft.cons.map((p) => String(p || "").trim()).filter(Boolean);
  if (Array.isArray(draft.consEn)) out.consEn = draft.consEn.map((p) => String(p || "").trim()).filter(Boolean);

  if (Array.isArray(draft.notFor)) {
    out.notFor = stringLinesFromMixedArray(draft.notFor);
  }
  if (draft.proTip !== undefined && draft.proTip !== null) out.proTip = String(draft.proTip || "").trim();

  if (Array.isArray(draft.neighborhoods)) {
    out.neighborhoods = draft.neighborhoods
      .map((n) => ({
        name: String(n?.name || "").trim(),
        description: String(n?.description || "").trim(),
        image: String(n?.image || "").trim(),
      }))
      .filter((n) => n.name);
  }

  if (draft.budget && typeof draft.budget === "object") {
    const b = draft.budget;
    const pick = (k) => {
      const v = b[k];
      if (v == null || v === "") return out.budget?.[k] ?? null;
      const n = Number(v);
      return Number.isFinite(n) ? n : out.budget?.[k] ?? null;
    };
    out.budget = {
      rent: pick("rent"),
      food: pick("food"),
      coworking: pick("coworking"),
      transport: pick("transport"),
    };
  }

  if (draft.climate && typeof draft.climate === "object") {
    const c = draft.climate;
    let highs =
      Array.isArray(c.monthlyHighs) && c.monthlyHighs.length
        ? c.monthlyHighs.map(Number).filter((x) => Number.isFinite(x))
        : [];
    if (!highs.length && Array.isArray(out.climate?.monthlyHighs)) highs = [...out.climate.monthlyHighs];
    let hm =
      c.highlightMonth != null && c.highlightMonth !== "" ? Number(c.highlightMonth) : out.climate?.highlightMonth ?? 4;
    if (!Number.isFinite(hm)) hm = out.climate?.highlightMonth ?? 4;
    out.climate = {
      bestTimeLabel: c.bestTimeLabel != null ? String(c.bestTimeLabel || "").trim() : out.climate?.bestTimeLabel || "",
      monthlyHighs: highs,
      highlightMonth: hm,
      tooltipTemp: c.tooltipTemp != null ? String(c.tooltipTemp || "").trim() : out.climate?.tooltipTemp || "",
    };
  }

  if (draft.travel && typeof draft.travel === "object") {
    const t = draft.travel;
    out.travel = {
      flightFrom: t.flightFrom != null ? String(t.flightFrom || "").trim() : out.travel?.flightFrom || "GRU",
      flightPrice:
        t.flightPrice != null && t.flightPrice !== ""
          ? Number(t.flightPrice) || 0
          : out.travel?.flightPrice ?? 0,
      flightDuration: t.flightDuration != null ? String(t.flightDuration || "").trim() : out.travel?.flightDuration || "",
      flightNote: t.flightNote != null ? String(t.flightNote || "").trim() : out.travel?.flightNote || "",
      visaEase: t.visaEase != null ? String(t.visaEase || "").trim() : out.travel?.visaEase || "",
      visaDuration: t.visaDuration != null ? String(t.visaDuration || "").trim() : out.travel?.visaDuration || "",
      timezoneOffset: t.timezoneOffset != null ? String(t.timezoneOffset || "").trim() : out.travel?.timezoneOffset || "",
      timezoneNote: t.timezoneNote != null ? String(t.timezoneNote || "").trim() : out.travel?.timezoneNote || "",
    };
  }

  if (Array.isArray(draft.vitalStats) && draft.vitalStats.length) {
    out.vitalStats = draft.vitalStats
      .map((st) => ({
        label: String(st?.label || "").trim(),
        value: Math.max(0, Math.min(10, Number(st?.value) || 0)),
        max: Number(st?.max) > 0 ? Number(st.max) : 10,
        display: String(st?.display || "").trim(),
      }))
      .filter((st) => st.label);
  }

  for (const k of ["planningLink", "communityLink", "guideLink", "ctaSubtitle", "heroVibeLine"]) {
    if (draft[k] !== undefined && draft[k] !== null) out[k] = String(draft[k] || "").trim();
  }

  if (Array.isArray(draft.essentialApps)) {
    out.essentialApps = draft.essentialApps
      .map((a) => ({
        name: String(a?.name || "").trim(),
        description: String(a?.description || "").trim(),
        link: String(a?.link || "").trim(),
      }))
      .filter((a) => a.name);
  }

  if (draft.visaSection && typeof draft.visaSection === "object") {
    const parsed = visaSectionFromLooseDraft(draft.visaSection);
    if (parsed) {
      out.visaSection = {
        visaSteps: parsed.visaSteps,
        visaLink: parsed.visaLink,
        visaCost: parsed.visaCost,
        visaDifficulty: parsed.visaDifficulty,
      };
    }
  }

  if (draft.arrival && typeof draft.arrival === "object") {
    const a = draft.arrival;
    out.arrival = {
      arrivalTips: Array.isArray(a.arrivalTips) ? stringLinesFromMixedArray(a.arrivalTips) : [],
      requiredDocuments: Array.isArray(a.requiredDocuments) ? stringLinesFromMixedArray(a.requiredDocuments) : [],
    };
  }

  if (typeof draft.communityEnabled === "boolean") out.communityEnabled = draft.communityEnabled;
  if (draft.communityDescription !== undefined && draft.communityDescription !== null) {
    out.communityDescription = String(draft.communityDescription || "").trim();
  }
  if (Array.isArray(draft.communityRules)) {
    out.communityRules = stringLinesFromMixedArray(draft.communityRules);
  }

  const merged = normalizeDestination(out);
  // Bloco "Visto" na City Page só aparece com visaSteps; modelos costumam preencher só travel.visaEase.
  if (merged?.visaSection && (!merged.visaSection.visaSteps || !merged.visaSection.visaSteps.length)) {
    const ease = String(merged.travel?.visaEase || "").trim();
    const dur = String(merged.travel?.visaDuration || "").trim();
    if (ease || dur) {
      const steps = [];
      if (ease) steps.push(`Resumo (consulte sempre a fonte oficial): ${ease}`);
      if (dur) steps.push(`Estadia / permissões (resumo): ${dur}`);
      steps.push(
        "Confirme documentos, taxas e elegibilidade no site da embaixada ou consulado da China (ou posto de vistos autorizado) antes de viajar.",
      );
      merged.visaSection = {
        ...merged.visaSection,
        visaSteps: steps,
      };
    }
  }
  return merged;
}

export function stringsToInsightItems(strs) {
  return (strs || []).map((s) => {
    const str = String(s || "").trim();
    const colon = str.indexOf(":");
    if (colon > 0 && colon < 80) {
      return { title: str.slice(0, colon).trim(), body: str.slice(colon + 1).trim() };
    }
    return { title: "", body: str };
  });
}

/**
 * Full body for POST/PUT /admin/destinations
 * @param {object} city normalized destination-shaped object
 * @param {{ syncProsConsToInsights?: boolean }} opts — use in My Account form so pros/cons stay source of truth
 */
export function buildDestinationAdminPayload(city, opts = {}) {
  if (!city) return null;
  let highlights = Array.isArray(city.highlights) ? city.highlights : [];
  let challenges = Array.isArray(city.challenges) ? city.challenges : [];
  if (opts.syncProsConsToInsights) {
    highlights = stringsToInsightItems(city.pros);
    challenges = stringsToInsightItems(city.cons);
  }
  const monthlyCost = Number(city.monthlyCost) || 0;
  const img0 = (city.heroImages && city.heroImages[0]) || city.imageUrl || "";
  const cc = String(city.countryCode || "")
    .trim()
    .toUpperCase()
    .slice(0, 2);
  return {
    name: city.name || "",
    country: city.country || "",
    countryCode: cc.length === 2 ? cc : "",
    region: city.region || "",
    imageUrl: img0,
    monthlyCost,
    scoreOverall: Number(city.scoreOverall) || 0,
    scoreCost: Number(city.scoreCost) || 0,
    scoreInternet: Number(city.scoreInternet) || 0,
    pros: insightToProsList(highlights),
    prosEn: Array.isArray(city.prosEn) ? city.prosEn : [],
    cons: insightToProsList(challenges),
    consEn: Array.isArray(city.consEn) ? city.consEn : [],
    description: city.description || "",
    descriptionEn: city.descriptionEn || "",
    tags: Array.isArray(city.tags) ? city.tags : [],
    heroImages: Array.isArray(city.heroImages) ? city.heroImages.filter((u) => String(u || "").trim()) : [],
    budget: {
      rent: city.budget?.rent != null ? Number(city.budget.rent) : null,
      food: city.budget?.food != null ? Number(city.budget.food) : null,
      coworking: city.budget?.coworking != null ? Number(city.budget.coworking) : null,
      transport: city.budget?.transport != null ? Number(city.budget.transport) : null,
    },
    highlights: highlights.map((h) => ({
      title: (h.title || "").trim(),
      body: (h.body || "").trim(),
    })),
    challenges: challenges.map((h) => ({
      title: (h.title || "").trim(),
      body: (h.body || "").trim(),
    })),
    notFor: stringLinesFromMixedArray(city.notFor),
    proTip: city.proTip || "",
    neighborhoods: (city.neighborhoods || [])
      .map((n) => ({
        name: (n.name || "").trim(),
        description: (n.description || "").trim(),
        image: (n.image || "").trim(),
      }))
      .filter((n) => n.name),
    climate: {
      bestTimeLabel: city.climate?.bestTimeLabel || "",
      monthlyHighs: Array.isArray(city.climate?.monthlyHighs) ? city.climate.monthlyHighs.map(Number) : [],
      highlightMonth: city.climate?.highlightMonth != null ? Number(city.climate.highlightMonth) : null,
      tooltipTemp: city.climate?.tooltipTemp || "",
    },
    travel: {
      flightFrom: city.travel?.flightFrom || "",
      flightPrice: Number(city.travel?.flightPrice) || 0,
      flightDuration: city.travel?.flightDuration || "",
      flightNote: city.travel?.flightNote || "",
      visaEase: city.travel?.visaEase || "",
      visaDuration: city.travel?.visaDuration || "",
      timezoneOffset: city.travel?.timezoneOffset || "",
      timezoneNote: city.travel?.timezoneNote || "",
    },
    vitalStats: (city.vitalStats || []).map((s) => ({
      label: (s.label || "").trim(),
      value: Number(s.value) || 0,
      max: Number(s.max) > 0 ? Number(s.max) : 10,
      display: (s.display || "").trim(),
    })),
    planningLink: city.planningLink || "",
    communityLink: city.communityLink || "",
    guideLink: city.guideLink || "",
    ctaSubtitle: city.ctaSubtitle || "",
    heroVibeLine: city.heroVibeLine || "",
    essentialApps: (city.essentialApps || []).map((a) => ({
      name: String(a?.name || "").trim(),
      description: String(a?.description || "").trim(),
      link: String(a?.link || "").trim(),
    })),
    visaSection: {
      visaSteps: (city.visaSection?.visaSteps || []).map((s) => String(s || "").trim()).filter(Boolean),
      visaLink: String(city.visaSection?.visaLink || "").trim(),
      visaCost: String(city.visaSection?.visaCost || "").trim(),
      visaDifficulty: String(city.visaSection?.visaDifficulty || "").trim(),
    },
    arrival: {
      arrivalTips: (city.arrival?.arrivalTips || []).map((s) => String(s || "").trim()).filter(Boolean),
      requiredDocuments: (city.arrival?.requiredDocuments || []).map((s) => String(s || "").trim()).filter(Boolean),
    },
    communityEnabled: !!city.communityEnabled,
    communityDescription: String(city.communityDescription || "").trim(),
    communityRules: (city.communityRules || []).map((s) => String(s || "").trim()).filter(Boolean),
    customSections: (city.customSections || []).map((s, idx) => ({
      id: s.id || 0,
      title: String(s.title || '').trim(),
      contentHtml: String(s.contentHtml || ''),
      order: idx,
    })),
  };
}
